<template>
    <div>
        <div class="row gx-3">
            <pc-input
                ref="modelPickerQuantityInput"
                v-model="quantity"
                v-bind:prepend-items="[quantityAbbreviation]"
                v-bind:validator="quantityValidator"
                v-bind:show-validation-errors="false"
                v-bind:validation-enabled="pvSystemElement.validationEnabled"
                v-bind:class="responsiveClasses.quantityInput"
                v-bind:readonly="readonly"
                v-bind:placeholder="quantityAbbreviation"
                v-bind:data-test-role="testRole.quantity"
                inputmode="numeric"
                lazy
                v-on:validated="onValidated('quantity', $event)" />
            <div v-bind:class="responsiveClasses.manufacturerSelect">
                <pc-filtered-select
                    v-model="manufacturerId"
                    v-bind:validator="manfAndModelValidator"
                    v-bind:show-validation-errors="false"
                    v-bind:validation-enabled="pvSystemElement.validationEnabled"
                    v-bind:alt-label="manfAltLabel"
                    v-bind:items="manufacturers"
                    v-bind:data-test-role="testRole.manufacturer"
                    v-bind:readonly="readonly"
                    v-bind:display-label="false"
                    value-prop="ManufacturerDefinitionId"
                    text-prop="ListedName"
                    v-on:validated="onValidated('manufacturer', $event)" />
            </div>
            <div v-bind:class="responsiveClasses.modelWrapper">
                <pc-transition transition="slide">
                    <pc-loader
                        v-show="hasManufacturer"
                        v-bind:loading="modelsLoading"
                        small>
                        <pc-filtered-select
                            v-model="modelId"
                            v-bind:validator="manfAndModelValidator"
                            v-bind:show-validation-errors="false"
                            v-bind:validation-enabled="pvSystemElement.validationEnabled"
                            v-bind:alt-label="modelAltLabel"
                            v-bind:items="dynamicModels"
                            v-bind:data-test-role="testRole.model"
                            v-bind:readonly="readonly"
                            v-bind:display-label="false"
                            value-prop="ModelId"
                            text-prop="Name"
                            v-on:validated="onValidated('model', $event)" />
                    </pc-loader>
                </pc-transition>
            </div>
            <div
                v-if="!readonly && canCopy && chosenModel"
                class="col-auto">
                <pc-loader
                    small
                    v-bind:loading="copyingComponent">
                    <pc-button-icon
                        v-bind:disabled="copyingComponent"
                        v-bind:link="copyButtonConfig.link"
                        v-bind:style="copyButtonConfig.style"
                        v-bind:icon="copyButtonConfig.iconName"
                        v-on:click="onCopyButtonClicked">
                        {{ componentCloneButtonLabel }}
                    </pc-button-icon>
                </pc-loader>
            </div>
        </div>
        <pc-transition transition="slide">
            <div
                v-if="chosenModel"
                class="col-12 col-lg-9">
                <div v-if="isInverter && programHasEssInverters">
                    <span class="fw-bold">{{ inverterTypeLabel }}</span
                    >&nbsp;<span data-test-role="inverter-type-descriptor">{{ modelDescriptor }}</span>
                </div>
                <div v-if="useEnhancedUi">
                    <div v-if="showInverterSmartStandardInfo">
                        <span class="fw-bold">{{ inverterSmartStandardLabel }}</span
                        >&nbsp;<span data-test-role="model-smart-standard">{{ chosenModel.Smart }}</span>
                    </div>
                    <div v-if="chosenModelHasInformation">
                        <div
                            v-for="info in chosenModelInformation"
                            v-bind:key="info">
                            <span
                                v-if="info.Label"
                                class="fw-bold"
                                >{{ info.Label }}&nbsp;</span
                            >{{ info.Value }}
                        </div>
                    </div>
                </div>
            </div>
        </pc-transition>
        <pc-transition transition="slide">
            <div
                v-if="chosenModel"
                class="col-12 col-lg-9">
                <strong>{{ chosenModelRatingLabel }}</strong>
                <pc-temp-space />
                <span data-test-role="model-display-value">{{ chosenModelRating }}</span>
                <pc-temp-space />
                <span>{{ chosenModelRatingUnit }}</span>
                <template v-if="!useEnhancedUi && showInverterSmartStandardInfo">
                    <strong class="ms-3">{{ inverterSmartStandardLabel }}</strong
                    >&nbsp;
                    <span data-test-role="model-smart-standard">{{ chosenModel.Smart }}</span>
                </template>
            </div>
        </pc-transition>
        <pc-transition transition="slide">
            <div
                v-if="chosenModel && chosenModelTechnology"
                class="col-12 col-lg-9">
                <strong>{{ chosenModelTechnologyLabel }}</strong>
                <pc-temp-space />
                <span data-test-role="model-technology">{{ chosenModelTechnology }}</span>
            </div>
        </pc-transition>
        <validation-render-helper
            v-bind:validation-error="validationErrors"
            class="mt-0 col-12 col-lg-9" />
    </div>
</template>

<script>
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import validationRenderHelper from "Validation/_validation-error-helper.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./modelpicker.i18n";
    import { COMPONENT_TYPE } from "Types/componentTypes";
    import { convertValueToString } from "Util/util";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        quantityInput: {
            // mb-3 mb-md-0 col-6 col-sm-3 col-md-2
            col: { xs: 6, sm: 3, md: 2 },
            mb: { xs: 3, md: 0 }
        },
        manufacturerSelect: {
            // mb-3 mb-md-0 col-12 col-sm-9 col-md-3
            col: { xs: 12, sm: 9, md: 3 },
            mb: { xs: 3, md: 0 }
        },
        modelWrapper: {
            // col-12 col-md-4
            col: { xs: 12, md: 4 }
        }
    };

    function mapComputeds(propNames) {
        const props = {};
        propNames.forEach((name) => {
            props[name] = {
                get() {
                    return this.spec[name];
                },
                set(value) {
                    switch (this.equipmentType) {
                        case COMPONENT_TYPE.Inverter:
                            this.pvSystemElement.setInverterProperty({
                                subsystemId: this.spec.id,
                                name,
                                value
                            });
                            break;

                        case COMPONENT_TYPE.PV_Module:
                            this.pvSystemElement.setPvModuleProperty({
                                subsystemId: this.parentId,
                                pvModuleId: this.spec.id,
                                name,
                                value
                            });
                            break;

                        case COMPONENT_TYPE.Battery:
                            this.pvSystemElement.setEquipmentItemProperty({
                                subsystemId: this.parentId,
                                equipmentItemId: this.spec.id,
                                name,
                                value
                            });
                            break;
                    }
                }
            };
        });
        return props;
    }

    // Indexes within the model.DisplayDetails list
    const displayDetailEquipmentModelRating = 0;
    const displayDetailEquipmentModelBatteryTechnology = 1;

    export default {
        components: { validationRenderHelper },
        inject: {
            pvSystemElement: {
                from: "pvSystemElement"
            },
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            spec: { type: Object, required: true },
            parentId: { type: String, required: false, default: "" },
            manufacturers: { type: Array, required: true },
            loadModels: { type: Function, required: true },
            dynamicModelFilter: { type: Function, required: false, default: null },
            equipmentType: { type: Number, required: false, default: COMPONENT_TYPE.Unspecified },
            useEnhancedUi: { type: Boolean, required: false, default: false },
            modelDescriptor: { type: String, required: false, default: "" },
            canCopy: { type: Boolean, required: true },
            readonly: { type: Boolean, required: false, default: false }
        },
        emits: ["copy-component"],
        data() {
            const quantityValidatorOptions = [
                { type: VALIDATOR_TYPES.required, overrideMsg: this.quantityRequiredValidatorMessage },
                {
                    type: VALIDATOR_TYPES.positiveInteger,
                    overrideMsg: this.quantityPositiveValidatorMessage
                }
            ];
            const quantityValidator = getValidator(this.$app, quantityValidatorOptions);
            const manfAndModelValidatorOptions = [
                { type: VALIDATOR_TYPES.required, overrideMsg: this.manfAndModelRequiredValidatorMessage }
            ];
            const manfAndModelValidator = getValidator(this.$app, manfAndModelValidatorOptions);
            const copyButtonConfig = {
                link: true,
                style: {
                    textDecoration: "none"
                },
                iconName: "copy"
            };
            return {
                models: [],
                dynamicModels: [],
                modelsLoading: false,
                copyButtonConfig,
                copyingComponent: false,
                quantityValidator,
                manfAndModelValidator,
                validationResults: {
                    quantity: null,
                    manufacturer: null,
                    model: null
                }
            };
        },
        computed: {
            isInverter() {
                return this.equipmentType === COMPONENT_TYPE.Inverter;
            },
            testRole() {
                let baseRole = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Inverter:
                        baseRole = "inverter";
                        break;
                    case COMPONENT_TYPE.PV_Module:
                        baseRole = "pv-array";
                        break;
                    case COMPONENT_TYPE.Battery:
                        baseRole = "equipment-item";
                        break;
                }
                return {
                    quantity: `${baseRole}-quantity`,
                    manufacturer: `${baseRole}-manufacturer-select`,
                    model: `${baseRole}-model-select`
                };
            },
            validationErrors() {
                const validationErrors = [];
                if (this.validationResults.quantity && this.validationResults.quantity.hasError) {
                    validationErrors.push(this.validationResults.quantity.error);
                }

                const manfHasError =
                    this.validationResults.manufacturer && this.validationResults.manufacturer.hasError;

                if (manfHasError) {
                    validationErrors.push(this.validationResults.manufacturer.error);
                }

                if (
                    this.validationResults.model &&
                    this.validationResults.model.hasError &&
                    !manfHasError // don't add duplicate message for missing manf/model
                ) {
                    validationErrors.push(this.validationResults.model.error);
                }

                return validationErrors;
            },
            programHasEssInverters() {
                return this.pvSystemElement.element.AuxiliaryModel.programUsesEssInverters;
            },
            hasInverterFiltering() {
                const filtering = this.pvSystemElement.element.Properties.inverterlistfiltering;
                return filtering !== "0" && Boolean(filtering);
            },
            hasManufacturer() {
                return Boolean(this.spec.manufacturerId);
            },
            selectedManufacturerName() {
                let manufName = "";
                const manufId = this.manufacturerId;
                const manuf = this.manufacturers.find(function (m) {
                    return m.ManufacturerDefinitionId === manufId;
                });
                if (manuf) {
                    manufName = manuf.ListedName;
                }
                return manufName;
            },
            selectModelPromptText() {
                let prompt = this.selectElementPromptText;
                if (this.dynamicModels && this.dynamicModels.length === 0) {
                    prompt =
                        this.hasInverterFiltering && this.models.length === 0
                            ? this.noFilteredModelsToSelectPrompt
                            : this.noModelsToSelectPrompt;
                }
                return prompt;
            },
            chosenModel() {
                if (this.spec.modelId && this.models.length > 0) {
                    const model = this.models.find((m) => m.ModelId.toString() == this.spec.modelId);
                    return model;
                }
                return null;
            },
            chosenModelRatingLabel() {
                let label = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Inverter:
                        label = this.inverterRatingLabel;
                        break;
                    case COMPONENT_TYPE.PV_Module:
                        label = this.moduleRatingLabel;
                        break;
                    case COMPONENT_TYPE.Battery:
                        label = this.batteryRatingLabel;
                        break;
                }
                return label;
            },
            chosenModelRatingUnit() {
                let unit = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Battery:
                        unit = this.modelPowerUnitKilowattHour;
                        break;
                }
                return unit;
            },
            showInverterSmartStandardInfo() {
                return (
                    this.isInverter &&
                    this.hasInverterFiltering &&
                    this.chosenModel.Smart &&
                    this.chosenModel.Smart.length > 0
                );
            },
            chosenModelInformation() {
                let info = [];
                if (this.chosenModel) {
                    if (this.chosenModel.ModelInformation) {
                        info = this.chosenModel.ModelInformation;
                    }
                }
                return info;
            },
            chosenModelHasInformation() {
                return this.chosenModelInformation.length > 0;
            },
            chosenModelTechnologyLabel() {
                let label = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Battery:
                        label = this.batteryTechnologyLabel;
                        break;
                }
                return label;
            },
            chosenModelTechnology() {
                let tech = "";
                if (this.equipmentType == COMPONENT_TYPE.Battery) {
                    tech = this.chosenModel.DisplayDetails[displayDetailEquipmentModelBatteryTechnology];
                }
                return tech;
            },
            chosenModelRating() {
                let rating = this.chosenModel.Rating;
                if (this.equipmentType == COMPONENT_TYPE.Battery) {
                    rating = this.chosenModel.DisplayDetails[displayDetailEquipmentModelRating];
                }
                if (convertValueToString(rating).trim() && isFinite(rating)) {
                    let digits = 0;
                    switch (this.equipmentType) {
                        case COMPONENT_TYPE.Inverter:
                            digits = 3;
                            break;
                        case COMPONENT_TYPE.PV_Module:
                            digits = 4;
                            break;
                        case COMPONENT_TYPE.Battery:
                            digits = 2;
                            break;
                    }
                    rating = Number(rating).toFixed(digits);
                }
                return rating;
            },
            ...mapComputeds(["quantity", "manufacturerId", "modelId", "energyRating", "equipmentTechnology"]),
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            ...mapI18n(
                {
                    ...KEYS,
                    noFilteredModelsToSelectPrompt() {
                        let key = KEYS.noFilteredModelsToSelectPromptTemplate;
                        const description = this.pvSystemElement.element.AuxiliaryModel.currentFilteringDescription;
                        if (description === "") {
                            key = KEYS.noModelsToSelectPrompt;
                        }
                        return {
                            key: key,
                            values: {
                                description: description
                            }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            modelId() {
                if (this.chosenModel && this.chosenModel.ManufacturerId != this.manufacturerId) {
                    this.$app.showErrorDialog(
                        this.manufModelMismatchDialogTitle,
                        this.manufModelMismatchDialogText,
                        true
                    );
                    this.modelId = "";
                    this.getModels();
                }

                if (this.chosenModel) {
                    if (
                        this.equipmentType == COMPONENT_TYPE.Battery &&
                        this.chosenModelRating &&
                        this.chosenModelRating != this.energyRating
                    ) {
                        this.energyRating = this.chosenModelRating;
                    }
                    if (this.chosenModelTechnology && this.chosenModelTechnology != this.equipmentTechnology) {
                        this.equipmentTechnology = this.chosenModelTechnology;
                    }
                } else {
                    if (this.energyRating) {
                        this.energyRating = "";
                    }
                    if (this.equipmentTechnology) {
                        this.equipmentTechnology = "";
                    }
                }
            },
            manufacturerId() {
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Inverter:
                        this.pvSystemElement.setInverterProperty({
                            subsystemId: this.spec.id,
                            name: "modelId",
                            value: ""
                        });
                        break;

                    case COMPONENT_TYPE.PV_Module:
                        this.pvSystemElement.setPvModuleProperty({
                            subsystemId: this.parentId,
                            pvModuleId: this.spec.id,
                            name: "modelId",
                            value: ""
                        });
                        break;

                    case COMPONENT_TYPE.Battery:
                        this.pvSystemElement.setEquipmentItemProperty({
                            subsystemId: this.parentId,
                            equipmentItemId: this.spec.id,
                            name: "modelId",
                            value: ""
                        });
                        break;
                }
                this.getModels();
            }
        },
        async created() {
            await this.getModels();
        },
        methods: {
            onValidated(prop, validationResult) {
                this.validationResults[prop] = validationResult;
            },
            async getModels() {
                if (this.hasManufacturer) {
                    try {
                        this.modelsLoading = true;
                        // Give the UI a chance to display the spinner for the model pc-loader
                        await this.$app.timeout(0);
                        const manufacturerId = parseInt(this.manufacturerId);
                        if (
                            this.equipmentType === COMPONENT_TYPE.Inverter ||
                            this.equipmentType === COMPONENT_TYPE.PV_Module
                        ) {
                            this.models = await this.loadModels(manufacturerId);
                        } else {
                            this.models = await this.loadModels(this.equipmentType, this.selectedManufacturerName);
                        }
                        await this.dynamicallyFilterModels(true);
                    } finally {
                        this.modelsLoading = false;
                    }
                }
            },
            async dynamicallyFilterModels(keepCurrentModel) {
                if (this.dynamicModelFilter) {
                    this.dynamicModels = this.dynamicModelFilter(this.models, keepCurrentModel ? this.modelId : null);
                } else {
                    this.dynamicModels = this.models;
                }
            },
            async onCopyButtonClicked() {
                this.copyingComponent = true;
                await this.$app.timeout(0);
                await this.$emit("copy-component");
                this.copyingComponent = false;
            },
            setFocusToQuantityInput() {
                this.$refs.modelPickerQuantityInput.focusIn();
            }
        }
    };
</script>
